// @flow

import React, { Component, type Node } from 'react';
import { connect } from 'react-redux';
import changeWindowLocation from 'app/common/lib/util/changeWindowLocation';
import DeepLink from 'app/common/react/components/growth/unauth/lib/DeepLink';
import ga from 'app/common/lib/ga';
import getClientTrackingParams from 'app/packages/client-tracking-params';
import HistoryManager from 'app/common/lib/HistoryManager';
import HistoryNavigator from 'app/common/lib/HistoryNavigator';
import NavigateData from 'app/common/lib/NavigateData';
import openExternalLinkFromPWA from 'app/common/react/components/Windows/openExternalLinkFromPWA';
import PropTypes from 'prop-types';
import pubSub from 'app/common/lib/PubSub';
import Router from 'app/common/lib/Router';
import SafeLink from 'app/common/lib/SafeLink';
import type { Router as RouterType } from 'app/common/lib/ReactRouter/types';
import type { State as LocationState } from 'app/common/redux/reducers/location';
import urlUtil from 'app/common/lib/urlUtil';
import withExperienceAnchor from 'app/common/lib/react/experiences/withExperienceAnchor';
import { compose } from 'redux';
import { withRouter } from 'react-router';

type ReduxProps = {|
  location: LocationState,
  pin?: *,
|};

type OwnProps = {|
  auxData?: Object,
  category?: string,
  children?: Node,
  className?: string,
  crsId?: string,
  deeplinkUrls?: {
    androidDeeplink: string,
    iOSDeeplink: string,
    webUrl?: string,
  },
  elementType?: number,
  external?: boolean,
  extraData?: {},
  forceDenzelNavigate?: boolean,
  focusOnBody?: boolean,
  forceOnClick?: boolean,
  forceRefresh?: number,
  gaCategory?: string,
  internalNewTab?: boolean,
  noFollow?: boolean,
  onClick?: (SyntheticEvent<>) => void,
  pinId?: string,
  router?: RouterType,
  shouldCaptureClick?: boolean,
  shouldShowAltLinks?: boolean,
  stopPropagation?: boolean,
  style?: {},
  target?: boolean,
  to: string,
  useNewTab?: boolean,
|};

type Props = {|
  ...OwnProps,
  ...ReduxProps,
|};

export class NavigateLink extends Component<Props> {
  /**
   * Set the focus on the body element after clicking a link
   * We do this for a11y & visual reasons
   * More info at https://stackoverflow.com/a/6976583/117193
   */
  setFocusOnBody = () => {
    if (window) {
      window.focus();
    }
    if (document.activeElement) {
      document.activeElement.blur();
    }
  };

  handleClick = (e: SyntheticMouseEvent<>) => {
    const {
      auxData,
      external: externalProp,
      focusOnBody,
      forceDenzelNavigate,
      forceOnClick,
      gaCategory,
      location,
      internalNewTab,
      onClick,
      pin,
      pinId,
      router: reactRouter,
      shouldShowAltLinks,
      stopPropagation: shouldStopPropagation,
      to: url,
    } = this.props;

    if (gaCategory) {
      ga.trackEvent(gaCategory, PCONST.GA_ACTION_CLICK);
    }

    if (shouldStopPropagation) {
      e.stopPropagation();
    }

    if (forceDenzelNavigate) {
      this.denzelNavigate();
      return;
    }

    const external = externalProp || urlUtil.isOffsiteUrl(url); // for parity with Denzel

    // Allow native command/control+click open in new tab for non-external links.
    if (!external && (e.metaKey || e.ctrlKey) && !forceOnClick) {
      // Note that:
      // 1. We are NOT triggering the click handler when user opens an internal link
      // in a new tab because the click handler may not take this case into account,
      // and does something with the false assumption that the user is navigating away.
      // The tradeoff here is that we may be missing logging for such events
      // TODO(jackhsu): figure out the best way to allow logging for such events.
      //
      // 2. We are ignoring shiftKey, altKey, and middle click for consistency w/ Denzel.
      // Ideally, we should allow them to be handled by the browser as well.
      return;
    }

    e.nativeEvent.preventDefault();

    if (onClick) {
      onClick(e);
    }

    // TODO(imad): reimplement signup modal functionality.
    // will keep this code snippet with TODO for fast reference.
    // if (url && url.indexOf('/login') !== -1 &&
    //    getInitModuleName() !== 'home_page' &&
    //    getInitModuleName() !== 'login_page' &&
    //    getInitModuleName() !== 'landing_page_analytics') {
    //    return signupModalManager.showInlineSignupModalOnButtonClick('LoginButton');
    // }

    if (!url || shouldShowAltLinks) {
      return;
    }

    // Using feature detection here
    if (external && typeof window !== 'undefined' && window.Windows) {
      // NOTE: Opening external links on Windows PWA need special
      // care if we want it to be opened in the user's browser.
      openExternalLinkFromPWA({ url, pinId, pin, location, auxData });
    } else if (external && pin) {
      const clientTrackingParams = getClientTrackingParams(pin, location);
      SafeLink.open(url, pinId, null, clientTrackingParams, auxData);
    } else if (external) {
      SafeLink.open(url, pinId);
    } else if (urlUtil.getUrlClass(url) === urlUtil.UrlClass.TRUSTED_DIFFERENT_ORIGIN) {
      changeWindowLocation(url, false);
    } else if (internalNewTab) {
      // Navigate to internal Pinterest page in new tab
      changeWindowLocation(url, true);
    } else if (reactRouter) {
      reactRouter.push(url);
      if (focusOnBody) {
        this.setFocusOnBody();
      }
    } else {
      this.denzelNavigate();
    }
  };

  denzelNavigate() {
    const {
      useNewTab,
      crsId,
      deeplinkUrls,
      extraData,
      focusOnBody,
      pinId,
      to: url,
      forceRefresh,
    } = this.props;

    if (forceRefresh === PCONST.ForceRefreshType.PAGE) {
      changeWindowLocation(url, false);
      return;
    }

    // if navigating to a closeup view while another navigation is already in
    // progress, we should cancel the later navigation. This to keep parity with
    // the navigate in Module.js
    const closeupUrl = new RegExp('/pin/[\\w|-]+/$');
    const reMatch = url.match(closeupUrl);
    if (reMatch && reMatch.index >= 0 && Router.instance.getStatus() !== Router.NavStatus.READY) {
      return;
    }

    const eventData = {
      crsId,
      forceRefresh,
      pinId,
      url,
      // Note that, regardless of origin, the URL is treated as external as long as the useNewTab prop is set. External
      // URLs are expected to be absolute (https://<domain>/pin/...), rather than relative (/pin/...)
      useNewTab,
    };

    pubSub.instance.publish(PCONST.PUBSUB_CHANNEL_SITE, PCONST.PUBSUB_TOPIC_NAVIGATE_STARTED, {
      url,
    });

    const deepLink = new DeepLink(this.context.userAgent.raw, this.context.userAgent.platform);
    if (
      deeplinkUrls &&
      deepLink.shouldDeepLink(
        document.referrer,
        this.context.userAgent,
        this.context.isAuthenticated,
      )
    ) {
      deepLink.navigateToDeepLink(this.context.unauthId, deeplinkUrls);
    }

    const _navigateData = new NavigateData();

    _navigateData.evt = { data: eventData };
    _navigateData.extraData = extraData;
    HistoryManager.singleton().populateNavigateData(_navigateData);
    HistoryNavigator.navigate(url, _navigateData);
    if (focusOnBody) {
      this.setFocusOnBody();
    }
  }

  render() {
    const {
      category,
      children,
      className,
      elementType,
      external,
      forceRefresh,
      noFollow,
      shouldCaptureClick,
      style,
      target,
      to,
    } = this.props;

    const linkProps = {
      className,
      'data-category': category,
      'data-force-refresh': forceRefresh,
      href: to,
      [shouldCaptureClick ? 'onClickCapture' : 'onClick']: this.handleClick,
      rel: external || noFollow ? 'nofollow' : '',
      style,
      target: target ? '_blank' : null,
    };

    if (elementType == null) {
      return <a {...linkProps}>{children}</a>;
    }
    return (
      <a {...linkProps} data-element-type={elementType}>
        {children}
      </a>
    );
  }
}

NavigateLink.propTypes = {
  shouldShowAltLinks: PropTypes.bool,
  category: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  crsId: PropTypes.string,
  deeplinkUrls: PropTypes.shape({
    androidDeeplink: PropTypes.string.isRequired,
    iOSDeeplink: PropTypes.string.isRequired,
    webUrl: PropTypes.string,
  }),
  elementType: PropTypes.number,
  external: PropTypes.bool,
  extraData: PropTypes.shape({}),
  focusOnBody: PropTypes.bool,
  forceOnClick: PropTypes.bool,
  forceRefresh: PropTypes.number,
  gaCategory: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    history: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  internalNewTab: PropTypes.bool,
  noFollow: PropTypes.bool,
  onClick: PropTypes.func,
  pin: PropTypes.shape({
    id: PropTypes.string,
    board: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
    pinner: PropTypes.shape({
      username: PropTypes.string.isRequired,
    }),
    tracking_params: PropTypes.string,
    tracking_params_map: PropTypes.shape({}),
  }),
  pinId: PropTypes.string,
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  stopPropagation: PropTypes.bool,
  style: PropTypes.shape({}),
  to: PropTypes.string,
  useNewTab: PropTypes.bool,
};

NavigateLink.contextTypes = {
  country: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  unauthId: PropTypes.string,
  userAgent: PropTypes.shape({
    canUseNativeApp: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired,
    isTablet: PropTypes.bool.isRequired,
    platform: PropTypes.string.isRequired,
    platformVersion: PropTypes.arrayOf(PropTypes.number),
    raw: PropTypes.string.isRequired,
  }),
};

export const NavigateLinkHome = compose(
  withExperienceAnchor('.HomeButton2'),
  withRouter,
)(NavigateLink);

export const NAV_MAP = {
  '.HomeButton2': NavigateLinkHome,
};

export default compose(
  withRouter,
  connect(
    ({ location, pins }, { pinId }): ReduxProps => ({
      location,
      pin: pinId && pins && pins[pinId],
    }),
  ),
)(NavigateLink);
